import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, Phone, Mail, MessageSquare, MessageCircle,
  StickyNote, PhoneIncoming, PhoneOutgoing, PhoneMissed, PhoneOff, Voicemail,
  UserX, ArrowDownUp, Loader2, ChevronDown, ChevronUp, Video
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  call:     { label: 'Appel',    icon: Phone,          color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400', dot: 'bg-blue-500' },
  sms:      { label: 'SMS',      icon: MessageSquare,   color: 'bg-green-500/10 text-green-600 dark:text-green-400', dot: 'bg-green-500' },
  email:    { label: 'Email',    icon: Mail,            color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400', dot: 'bg-purple-500' },
  whatsapp: { label: 'WhatsApp', icon: MessageCircle,   color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', dot: 'bg-emerald-500' },
  note:     { label: 'Note',     icon: StickyNote,      color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400', dot: 'bg-amber-500' },
  meeting:  { label: 'Reunion',  icon: Phone,           color: 'bg-rose-500/10 text-rose-600 dark:text-rose-400', dot: 'bg-rose-500' },
};

const FILTERS = [
  { value: '', label: 'Tous' },
  { value: 'call', label: 'Appels' },
  { value: 'teams', label: 'Teams' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

const PAGE_SIZE = 30;

function formatDate(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function formatWhatsAppContact(contactName, source) {
  // Format raw WhatsApp IDs like "33612345678@s.whatsapp.net" to "+33 6 12 34 56 78"
  const raw = contactName || source || '';
  const waMatch = raw.match(/^(\d+)@s\.whatsapp\.net$/);
  if (waMatch) {
    const num = waMatch[1];
    return '+' + num.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  }
  // Also handle plain long numbers without @
  if (/^\d{10,15}$/.test(raw)) {
    return '+' + raw.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  }
  return contactName;
}

function groupByDate(memories) {
  const groups = {};
  memories.forEach(m => {
    const key = new Date(m.timestamp).toLocaleDateString('fr-FR');
    if (!groups[key]) groups[key] = { label: formatDate(m.timestamp), items: [] };
    groups[key].items.push(m);
  });
  return Object.values(groups);
}

const CALL_STATUS_CONFIG = {
  unanswered:      { label: 'Non abouti',       icon: PhoneMissed, className: 'bg-red-500/10 text-red-600 dark:text-red-400' },
  voicemail:       { label: 'Messagerie',        icon: Voicemail,   className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  unknown_contact: { label: 'Contact inconnu',   icon: UserX,       className: 'bg-zinc-500/10 text-zinc-500 dark:text-zinc-400' },
};

function CallStatusBadge({ memory }) {
  if (memory.type !== 'call') return null;
  const status = memory.call_status;
  const cfg = CALL_STATUS_CONFIG[status];
  if (!cfg) return null; // completed = no badge needed
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded ${cfg.className}`} data-testid={`call-status-${status}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

function DirectionBadge({ memory }) {
  if (memory.type !== 'call' && memory.type !== 'sms') return null;
  // Use direction field if available (set by backfill)
  const dir = memory.direction;
  const text = memory.memory_text || '';
  const summary = memory.summary || '';
  const isInbound = dir === 'inbound' || /\b(reçu|entrant|inbound|incoming)\b/i.test(text) || /\b(reçu)\b/i.test(summary);
  const isOutbound = dir === 'outbound' || /\b(sortant|outbound|outgoing|envoyé|sent)\b/i.test(text) || /\b(envoyé|sortant)\b/i.test(summary);
  if (!isInbound && !isOutbound) return null;
  return isInbound ? (
    <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-600 dark:text-sky-400">
      <PhoneIncoming className="w-3 h-3" /> Entrant
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-600 dark:text-orange-400">
      <PhoneOutgoing className="w-3 h-3" /> Sortant
    </span>
  );
}

function SourceBadge({ memory }) {
  const src = memory.source || '';
  if (src.startsWith('teams:')) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
        <Video className="w-3 h-3" /> Teams
      </span>
    );
  }
  return null;
}

export default function TimelinePage() {
  const { personId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [person, setPerson] = useState(null);
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    fetchPerson();
  }, [personId]);

  useEffect(() => {
    setMemories([]);
    setHasMore(true);
    fetchMemories(0);
  }, [personId, typeFilter, searchDebounced]);

  const fetchPerson = async () => {
    try {
      const r = await axios.get(`${API}/persons/${personId}`, { headers: { Authorization: `Bearer ${token}` } });
      setPerson(r.data);
    } catch {
      toast.error('Clone introuvable');
      navigate('/clones');
    }
  };

  const fetchMemories = useCallback(async (skip = 0) => {
    if (skip === 0) setLoading(true); else setLoadingMore(true);
    try {
      const params = { skip, limit: PAGE_SIZE };
      if (typeFilter) params.type = typeFilter;
      if (searchDebounced) params.search = searchDebounced;
      const r = await axios.get(`${API}/persons/${personId}/memories`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      if (skip === 0) {
        setMemories(r.data);
      } else {
        setMemories(prev => [...prev, ...r.data]);
      }
      setHasMore(r.data.length === PAGE_SIZE);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [personId, token, typeFilter, searchDebounced]);

  const loadMore = () => fetchMemories(memories.length);

  const groups = groupByDate(memories);

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="timeline-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/clones')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-heading font-bold">Timeline</h1>
            {person && <p className="text-sm text-muted-foreground">{person.name}</p>}
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate(`/clones/${personId}/chat`)} className="gap-1.5">
            <MessageSquare className="w-4 h-4" /> Chat
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3" data-testid="timeline-filters">
          <div className="flex gap-1.5 flex-wrap">
            {FILTERS.map(f => (
              <Button
                key={f.value}
                variant={typeFilter === f.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTypeFilter(f.value)}
                data-testid={`filter-${f.value || 'all'}`}
              >
                {f.value && TYPE_CONFIG[f.value] && (() => { const Icon = TYPE_CONFIG[f.value].icon; return <Icon className="w-3.5 h-3.5 mr-1" />; })()}
                {f.label}
              </Button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
              data-testid="timeline-search"
            />
          </div>
        </div>

        {/* Timeline */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : memories.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-muted-foreground">
              <ArrowDownUp className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>Aucune interaction trouvee{typeFilter ? ` de type "${FILTERS.find(f=>f.value===typeFilter)?.label}"` : ''}{searchDebounced ? ` pour "${searchDebounced}"` : ''}.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8" data-testid="timeline-content">
            {groups.map((group, gi) => (
              <div key={gi}>
                {/* Date header */}
                <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm py-2 mb-4">
                  <span className="text-sm font-medium text-muted-foreground capitalize">{group.label}</span>
                </div>
                {/* Items */}
                <div className="relative pl-8 space-y-1">
                  {/* Vertical line */}
                  <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border" />
                  {group.items.map((m, i) => {
                    const cfg = TYPE_CONFIG[m.type] || TYPE_CONFIG.note;
                    const Icon = cfg.icon;
                    const isExpanded = expandedId === m.id;
                    const hasFullText = m.memory_text && m.memory_text.length > 0;
                    return (
                      <div key={m.id} className="relative group" data-testid={`timeline-item-${m.id}`}>
                        {/* Dot */}
                        <div className={`absolute -left-8 top-3 w-[22px] h-[22px] rounded-full flex items-center justify-center ${cfg.color} ring-2 ring-background`}>
                          <Icon className="w-3 h-3" />
                        </div>
                        {/* Card */}
                        <div
                          className={`py-2 px-4 rounded-lg transition-colors ${hasFullText ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'}`}
                          onClick={() => hasFullText && setExpandedId(isExpanded ? null : m.id)}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-muted-foreground font-mono">{formatTime(m.timestamp)}</span>
                            <Badge variant="secondary" className={`text-[11px] px-1.5 py-0 ${cfg.color}`}>{cfg.label}</Badge>
                            <DirectionBadge memory={m} />
                            <SourceBadge memory={m} />
                            <CallStatusBadge memory={m} />
                            {m.contact_name && (
                              <span className="text-sm font-medium">
                                {m.type === 'whatsapp' ? formatWhatsAppContact(m.contact_name, m.source) : m.contact_name}
                              </span>
                            )}
                            {m.importance === 'high' || m.importance === 'critical' ? (
                              <Badge variant="destructive" className="text-[10px] px-1 py-0">Important</Badge>
                            ) : null}
                            {hasFullText && (
                              <span className="ml-auto text-muted-foreground/50">
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </span>
                            )}
                          </div>
                          <p className={`text-sm text-muted-foreground mt-1 ${isExpanded ? '' : 'line-clamp-2'}`}>{m.summary}</p>
                          {/* Expanded: full content */}
                          {isExpanded && (
                            <div className="mt-3 p-3 rounded-md bg-muted/60 border border-border" data-testid={`detail-${m.id}`}>
                              <p className="text-xs font-medium text-muted-foreground mb-2">Contenu complet</p>
                              <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.memory_text}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {/* Load more */}
            {hasMore && (
              <div className="flex justify-center pt-4">
                <Button variant="outline" onClick={loadMore} disabled={loadingMore} data-testid="load-more-btn">
                  {loadingMore ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Charger plus
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
